import { chain, forEach, isEmpty } from 'lodash';
import { format, startOfDay } from 'date-fns';
import i18n from 'i18next';
import mapBackendErrorsToUserFields, { mapBackendErrorsToCalculatorFields } from '../../validations/backendErrors';
import {
  APPLICATION_CONTRACT_TYPES,
  ARMENIAN_TIME_ZONE_HOURS,
  CONTRACT_END_ISO_TIME,
  DATE_FORMAT,
} from '../../constants';
import { getArmenianLocalDate, replaceTimePortion, replaceTimeZone } from '../dates';
import { ROAD_CARE_PLAN_TYPES } from '../../constants/roadCare';
import { DEFAULT_CURRENCY } from '../../config';
import { PAYMENT_FREQUENCIES } from '../../constants/paymentFrequency';

// @TODO need to adjust handler with roadCare!
export const mapErrorsToContractSteps = (errors) => {
  let signerBackendErrors;
  let calculatorBackendErrors;
  let nonInsuredPeopleErrors = [];

  forEach(errors, (error) => {
    nonInsuredPeopleErrors.push(error);
  });
  nonInsuredPeopleErrors = chain(nonInsuredPeopleErrors)
    .flatten()
    .uniq()
    .value();

  signerBackendErrors = mapBackendErrorsToUserFields(nonInsuredPeopleErrors);
  calculatorBackendErrors = mapBackendErrorsToCalculatorFields(
    nonInsuredPeopleErrors,
  );

  if (isEmpty(signerBackendErrors)) {
    signerBackendErrors = null;
  }

  if (isEmpty(calculatorBackendErrors)) {
    calculatorBackendErrors = null;
  }

  return {
    signerBackendErrors,
    calculatorBackendErrors,
  };
};

export const bindRoadCareContractAndSaveInLocal = (contract, countries, states, cityVillages) => {
  const contractData = {
    signer: {
      ...contract.signer,
      citizenship: countries.find((country) => country.id === contract.signer.citizenshipId),
      ...contract.signer.contactInfo,
      country: countries.find((country) => country.id === contract.signer.contactInfo.countryId),
      state: states.find((state) => state.id === contract.signer.contactInfo.stateId),
      cityVillage: cityVillages.find(
        (cityVillage) => cityVillage.id === contract.signer.contactInfo.cityVillageId,
      ),
      cellPhone: `0${contract.signer.contactInfo.cellPhone.slice(4)}`,
      resident: contract.signer.resident ? 1 : 0,
    },
    roadCareDetails: {
      paymentFrequency: PAYMENT_FREQUENCIES[contract.paymentFrequency],
      riskAmount: contract.plan.amount,
      transportUsageType: contract.transportUsageType,
      range: {
        start: new Date(contract.contractData.contractStart),
        end: new Date(contract.contractData.contractEnd),
      },
    },
  };

  return {
    contractData,
    searchVehicleFieldsData: {
      passport: contract.vehicles[0].passport,
      plateNumber: contract.vehicles[0].plateNumber,
      hasAgreed: true,
    },
    paymentMethod: contract.paymentType,
    isSaveCard: contract.isSaveCard,
    draftToken: contract.draftToken,
  };
};

export const bindRoadCareContractToApiFormat = (contract) => {
  const { signer } = contract;
  signer.birthday = format(new Date(signer.birthday), DATE_FORMAT);
  signer.passportGiven = format(new Date(signer.passportGiven), DATE_FORMAT);
  signer.passportValidUntil = format(new Date(signer.passportValidUntil), DATE_FORMAT);

  const userDetails = signer;
  const convertedContract = {};
  convertedContract.firstName = userDetails.firstName;
  convertedContract.lastName = userDetails.lastName;
  convertedContract.lastNameEn = userDetails.lastNameEn;
  convertedContract.firstNameEn = userDetails.firstNameEn;
  convertedContract.gender = userDetails.gender;
  convertedContract.birthday = userDetails.birthday;
  convertedContract.socCard = userDetails.socCard || '';
  convertedContract.citizenshipId = userDetails.citizenship.id;
  convertedContract.resident = true;
  convertedContract.passport = userDetails.passport;
  convertedContract.passportGiven = userDetails.passportGiven;
  convertedContract.passportValidUntil = userDetails.passportValidUntil;
  convertedContract.passportFrom = userDetails.passportFrom;

  convertedContract.contactInfo = {
    cellPhone: `+374${userDetails.cellPhone.slice(1)}`,
    phone: userDetails.phone,
    email: userDetails.email,
    countryId: userDetails.country.id,
    street: userDetails.street,
    stateId: userDetails.state?.id ?? null,
    streetEn: userDetails.streetEn ?? '',
    bldNumber: userDetails.bldNumber,
    apt: userDetails.apt,
    additional: userDetails.additional,
    cityVillageId: userDetails.cityVillage?.id ?? null,
  };

  return {
    signer: convertedContract,
    type: APPLICATION_CONTRACT_TYPES.ROAD_CARE,
    contractData: {
      contractStart: replaceTimeZone(
        format(contract.roadCareDetails.range.start, DATE_FORMAT, new Date(getArmenianLocalDate())),
        ARMENIAN_TIME_ZONE_HOURS,
      ),
      contractEnd: replaceTimePortion(replaceTimeZone(
        format((contract.roadCareDetails.range.end), DATE_FORMAT, new Date(getArmenianLocalDate())),
        ARMENIAN_TIME_ZONE_HOURS,
      ), CONTRACT_END_ISO_TIME),
      contractSignDate: replaceTimeZone(
        format(
          startOfDay(new Date(getArmenianLocalDate())),
          DATE_FORMAT,
          new Date(getArmenianLocalDate()),
        ),
        ARMENIAN_TIME_ZONE_HOURS,
      ),
    },
    isSaveCard: false,
    isAutomaticRecurringPayment: false,
    paymentFrequency: contract.roadCareDetails.paymentFrequency.value,
    vehicles: [{
      passport: contract.searchVehicleFieldsData.passport,
      riskAmount: contract.roadCareDetails.riskAmount,
      vehicleType: contract.roadCareDetails.transportUsageType.key,
      vehicleDate: contract.ekengVehicleData.released,
      plateNumber: contract.searchVehicleFieldsData.plateNumber,
    }],
    plan: {
      type: ROAD_CARE_PLAN_TYPES.DEFAULT,
      coverages: [],
      currency: DEFAULT_CURRENCY.toLowerCase(),
      amount: contract.roadCareDetails.riskAmount,
    },
    transportUsageType: contract.roadCareDetails.transportUsageType,
    language: i18n.language,
    mobileToken: contract.mobileToken,
  };
};
